import { ButtonColors } from '@chiroup/components/Button';
import { FieldValidators } from '@chiroup/core/constants/DatabaseFeatureCommon';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { Clinic, ListClinic } from '@chiroup/core/types/Clinic.type';
import { useForm } from '@chiroup/hooks/useForm';
import dayjs from 'dayjs';
import React, { use, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import clinicService from '../../services/clinic.service';
import Modal from '../common/Modal';
import ClinicGeneralFormElements from './detail/ClinicGeneralFormElements';

const validation = {
  name: {
    required: {
      message: 'Clinic name is required.',
    },
  },
  plan: {
    required: {
      message: 'Plan is required',
    },
  },
  phone: FieldValidators.phone(),
  userEmail: {
    required: {
      message: 'A primary user email is required.',
    },
    function: {
      value: (value: Partial<ListClinic & { userEmail?: string }>) => {
        const email = value.userEmail?.trim();

        if (!email) return false;

        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        return isValid ? false : 'Enter a valid email address.';
      },
    },
  },
  userFname: {
    required: {
      message: 'First name is required.',
    },
  },
  userLname: {
    required: {
      message: 'Last name is required.',
    },
  },
};

type Props = {
  state: OpenClosedStates;
  close: (val?: Clinic) => void;
  onCreate: () => void;
  // lastName?: string;
  user: any;
};

type ClinicCreateFormValue = ListClinic & {
  userEmail?: string;
  userTitle?: string;
  userFname?: string;
  userLname?: string;
};

const ClinicCreateModal: React.FC<Props> = ({
  state,
  close,
  onCreate,
  // lastName,
  user,
}) => {
  const { value, registerSubmit, isDirty, errors, onChange, patchValue } =
    useForm<ClinicCreateFormValue>(
      {
        surveyOptOut: false,
        theme: 'default-theme',
        plan: 'trial',
        billDate: dayjs().add(2, 'w').format('MM/DD/YYYY'),
        premiumUntil: '',
        plusUntil: '',
        userTitle: 'Dr',
      },
      validation
    );
  const [saving, setSaving] = useState(false);
  const { createToast } = use(ToastContext);

  const onSubmit = async (clinic: Partial<ClinicCreateFormValue>) => {
    setSaving(true);
    const res = await clinicService.create(clinic);
    setSaving(false);
    close(res);
    onCreate();

    return res;
  };

  const onSuccess = (data: Partial<ListClinic>) => {
    createToast({
      title: 'Successfully created new clinic!',
      description: (
        <>
          Click{' '}
          <Link
            to={`/clinics/${data.ID}`}
            className="text-primary-600 font-semibold hover:text-primary-500"
          >
            here
          </Link>{' '}
          to view the clinic.
        </>
      ),
      type: ToastTypes.Success,
      duration: 5000,
    });
    close();
    onCreate?.();
  };

  const onFail = (err: any) => {
    console.error({ err });
    createToast({
      title: 'Failed to create clinic',
      description: err?.response?.data?.message || 'An error occurred while creating the clinic.',
      type: ToastTypes.Fail,
      duration: 5000,
    });
    setSaving(false);
  };

  const footerButtons = [
    {
      text: 'Close',
      onClick: () => close(),
      color: ButtonColors.plain,
      className: 'border border-gray-300 dark:border-dark-gray-500',
    },
    {
      text: 'Create',
      onClick: registerSubmit(onSubmit, {
        onSuccess,
        onFail,
      }),
      loading: saving,
      disabled: !isDirty,
    },
  ];

  return (
    <Modal
      isOpen={state === OpenClosedStates.Open}
      close={close}
      header={{ title: 'Add clinic' }}
      footer={{ buttons: footerButtons }}
    >
      <div className="space-y-1 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4 sm:py-5">
        <ClinicGeneralFormElements
          fieldErrors={errors.fieldErrors}
          value={value}
          onChange={onChange}
          patchValue={patchValue}
          user={user}
        />
      </div>
    </Modal>
  );
};

export default ClinicCreateModal;
